import React, { lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layouts
import MainLayout from "./components/layout/MainLayout";

// Public Pages (loaded immediately)
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import LegalPages from "./pages/LegalPages";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";

// === LAZY LOADED PAGES (Performance Optimization) ===
// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const CoproprietesPage = lazy(() => import("./pages/admin/CoproprietesPage"));
const AgentsPage = lazy(() => import("./pages/admin/AgentsPage"));
const SecteursPage = lazy(() => import("./pages/admin/SecteursPage"));
const OpportunitesPage = lazy(() => import("./pages/admin/OpportunitesPage"));
const SyndicsPage = lazy(() => import("./pages/admin/SyndicsPage"));
const ImportPage = lazy(() => import("./pages/admin/ImportPage"));
const CEESettingsPage = lazy(() => import("./pages/admin/CEESettingsPage"));
const GamificationSettingsPage = lazy(() => import("./pages/admin/GamificationSettingsPage"));
const ProspectRulesPage = lazy(() => import("./pages/admin/ProspectRulesPage"));
const SupportPage = lazy(() => import("./pages/admin/SupportPage"));
const MonEntreprisePage = lazy(() => import("./pages/admin/MonEntreprisePage"));
const AdminAgendaPage = lazy(() => import("./pages/admin/AgendaPage"));
const EmailTemplatesPage = lazy(() => import("./pages/admin/EmailTemplatesPage"));
const GestionFormulaireEligibilitePage = lazy(() => import("./pages/admin/GestionFormulaireEligibilitePage"));
const ReponsesEligibilitePage = lazy(() => import("./pages/admin/ReponsesEligibilitePage"));
const GestionModelesDocumentsPage = lazy(() => import("./pages/admin/GestionModelesDocumentsPage"));
const GestionFormulairesPage = lazy(() => import("./pages/admin/GestionFormulairesPage"));
const ConfigurationIAPage = lazy(() => import("./pages/admin/ConfigurationIAPage"));
const PersonnalisationDocumentsPage = lazy(() => import("./pages/admin/PersonnalisationDocumentsPage"));
const DataSyncPage = lazy(() => import("./pages/admin/DataSyncPage"));

// Super Admin Pages
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/SuperAdminDashboard"));
const LegalPagesEditorPage = lazy(() => import("./pages/superadmin/LegalPagesEditorPage"));
const SettingsPage = lazy(() => import("./pages/superadmin/SettingsPage"));
const ImportRNICPage = lazy(() => import("./pages/superadmin/ImportRNICPage"));
const CompaniesManagementPage = lazy(() => import("./pages/superadmin/CompaniesManagementPage"));
const SubscriptionProductsPage = lazy(() => import("./pages/superadmin/SubscriptionProductsPage"));
const GestionRolesPage = lazy(() => import("./pages/superadmin/GestionRolesPage"));

// Agent Pages
const AgentDashboard = lazy(() => import("./pages/agent/AgentDashboard"));
const AgentCoproprietesPage = lazy(() => import("./pages/agent/AgentCoproprietesPage"));
const AgentOpportunitesPage = lazy(() => import("./pages/agent/AgentOpportunitesPage"));
const RechercheUnifiee = lazy(() => import("./pages/agent/RechercheUnifiee"));
const MaProspectionPage = lazy(() => import("./pages/agent/MaProspectionPage"));
const AgentProfilPage = lazy(() => import("./pages/agent/AgentProfilPage"));
const AgendaPage = lazy(() => import("./pages/agent/AgendaPage"));

// Travaux Pages
const TravauxDashboard = lazy(() => import("./pages/travaux/TravauxDashboard"));
const ProjetsTravauxPage = lazy(() => import("./pages/travaux/ProjetsTravauxPage"));
const ProjetTravauxDetailPage = lazy(() => import("./pages/travaux/ProjetTravauxDetailPage"));
const SousTraitantsPage = lazy(() => import("./pages/travaux/SousTraitantsPage"));
const DelegatairesCEEPage = lazy(() => import("./pages/travaux/DelegatairesCEEPage"));
const LicenceTravauxPage = lazy(() => import("./pages/travaux/LicenceTravauxPage"));
const PlanningGanttPage = lazy(() => import("./pages/travaux/PlanningGanttPage"));
const DocumentsGEDPage = lazy(() => import("./pages/travaux/DocumentsGEDPage"));
const AgendaChantierPage = lazy(() => import("./pages/travaux/AgendaChantierPage"));

// Mobile Components
import MobileBottomNav from "./components/layout/MobileBottomNav";

// Loading Spinner for lazy components
const LazyLoadingSpinner = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#64748B] text-sm">Chargement...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole, allowedRoles }) => {
  const { user, loading, activePortal } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Determine effective role (considering portal selection for dual-role users)
  const effectiveRole = activePortal || user.role;

  // Check if user role is allowed
  if (allowedRoles && !allowedRoles.includes(effectiveRole) && !allowedRoles.includes(user.role)) {
    // Redirect based on effective role (considers portal selection)
    if (user.role === 'super_admin' && !activePortal) return <Navigate to="/super-admin" replace />;
    if (effectiveRole === 'admin' || user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/agent" replace />;
  }

  if (requiredRole) {
    // Super admin check - strict (only for super_admin-only pages)
    if (requiredRole === 'super_admin' && user.role !== 'super_admin') {
      if (user.role === 'admin') return <Navigate to="/admin" replace />;
      return <Navigate to="/agent" replace />;
    }
    
    // Admin check - allow if user is admin OR super_admin with admin portal
    if (requiredRole === 'admin' && user.role !== 'admin') {
      // Super admin can access admin portal if they selected it
      if (user.role === 'super_admin' && activePortal === 'admin') {
        return children;
      }
      if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
      return <Navigate to="/agent" replace />;
    }
    
    // Agent check - allow if user is agent OR admin/super_admin with agent portal
    if (requiredRole === 'agent') {
      if (user.role === 'agent') {
        return children;
      }
      // Admin with agent portal selected
      if (user.role === 'admin' && activePortal === 'agent') {
        return children;
      }
      // Super admin with agent portal selected
      if (user.role === 'super_admin' && activePortal === 'agent') {
        return children;
      }
      if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
      if (user.role === 'admin') return <Navigate to="/admin" replace />;
    }
  }

  return children;
};

// Travaux Protected Route - Only for admin/super_admin roles
const TravauxProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Seuls les admins et super_admins peuvent accéder au module Travaux
  if (user.role !== 'admin' && user.role !== 'super_admin') {
    // Rediriger vers le portail agent
    return <Navigate to="/agent" replace />;
  }

  return children;
};

// Auth Redirect Component
const AuthRedirect = () => {
  const { user, loading, activePortal, showPortalSelector, availableRoles } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    // If user has multiple roles and no portal selected yet, show portal selector
    if (availableRoles && availableRoles.length > 1 && !activePortal && showPortalSelector) {
      // Navigate to admin but portal selector will be shown there
      return <Navigate to="/admin" replace />;
    }
    
    // Super admin - check if they selected a different portal
    if (user.role === 'super_admin') {
      if (activePortal === 'agent') return <Navigate to="/agent" replace />;
      if (activePortal === 'admin') return <Navigate to="/admin" replace />;
      if (activePortal === 'travaux') return <Navigate to="/travaux" replace />;
      return <Navigate to="/super-admin" replace />;
    }
    // If admin with agent portal selected, go to agent
    if (user.role === 'admin' && activePortal === 'agent') return <Navigate to="/agent" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/agent" replace />;
  }

  return <LoginPage />;
};

// Main Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthRedirect />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      
      {/* Legal Pages */}
      <Route path="/legal" element={<LegalPages />} />
      <Route path="/legal/:page" element={<LegalPages />} />
      
      {/* Super Admin Routes */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requiredRole="super_admin">
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<LazyLoadingSpinner />}><SuperAdminDashboard /></Suspense>} />
        <Route path="import-rnic" element={<Suspense fallback={<LazyLoadingSpinner />}><ImportRNICPage /></Suspense>} />
        <Route path="companies" element={<Suspense fallback={<LazyLoadingSpinner />}><CompaniesManagementPage /></Suspense>} />
        <Route path="legal-pages" element={<Suspense fallback={<LazyLoadingSpinner />}><LegalPagesEditorPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<LazyLoadingSpinner />}><SettingsPage /></Suspense>} />
        <Route path="subscription-products" element={<Suspense fallback={<LazyLoadingSpinner />}><SubscriptionProductsPage /></Suspense>} />
        <Route path="roles" element={<Suspense fallback={<LazyLoadingSpinner />}><GestionRolesPage /></Suspense>} />
      </Route>
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<LazyLoadingSpinner />}><AdminDashboard /></Suspense>} />
        <Route path="coproprietes" element={<Suspense fallback={<LazyLoadingSpinner />}><CoproprietesPage /></Suspense>} />
        <Route path="agents" element={<Suspense fallback={<LazyLoadingSpinner />}><AgentsPage /></Suspense>} />
        <Route path="opportunites" element={<Suspense fallback={<LazyLoadingSpinner />}><OpportunitesPage /></Suspense>} />
        <Route path="syndics" element={<Suspense fallback={<LazyLoadingSpinner />}><SyndicsPage /></Suspense>} />
        <Route path="base-nationale" element={<Suspense fallback={<LazyLoadingSpinner />}><ImportPage /></Suspense>} />
        <Route path="import" element={<Suspense fallback={<LazyLoadingSpinner />}><ImportPage /></Suspense>} />
        <Route path="cee-settings" element={<Suspense fallback={<LazyLoadingSpinner />}><CEESettingsPage /></Suspense>} />
        <Route path="gamification" element={<Suspense fallback={<LazyLoadingSpinner />}><GamificationSettingsPage /></Suspense>} />
        <Route path="prospect-rules" element={<Suspense fallback={<LazyLoadingSpinner />}><ProspectRulesPage /></Suspense>} />
        <Route path="support" element={<Suspense fallback={<LazyLoadingSpinner />}><SupportPage /></Suspense>} />
        <Route path="mon-entreprise" element={<Suspense fallback={<LazyLoadingSpinner />}><MonEntreprisePage /></Suspense>} />
        <Route path="registre" element={<Suspense fallback={<LazyLoadingSpinner />}><RechercheUnifiee /></Suspense>} />
        <Route path="agenda" element={<Suspense fallback={<LazyLoadingSpinner />}><AdminAgendaPage /></Suspense>} />
        <Route path="emails" element={<Suspense fallback={<LazyLoadingSpinner />}><EmailTemplatesPage /></Suspense>} />
        <Route path="formulaires" element={<Suspense fallback={<LazyLoadingSpinner />}><GestionFormulairesPage /></Suspense>} />
        <Route path="eligibilite/formulaire" element={<Suspense fallback={<LazyLoadingSpinner />}><GestionFormulaireEligibilitePage /></Suspense>} />
        <Route path="eligibilite/formulaire/:formulaireId" element={<Suspense fallback={<LazyLoadingSpinner />}><GestionFormulaireEligibilitePage /></Suspense>} />
        <Route path="eligibilite/reponses" element={<Suspense fallback={<LazyLoadingSpinner />}><ReponsesEligibilitePage /></Suspense>} />
        <Route path="modeles-documents" element={<Suspense fallback={<LazyLoadingSpinner />}><GestionModelesDocumentsPage /></Suspense>} />
        <Route path="ia-config" element={<Suspense fallback={<LazyLoadingSpinner />}><ConfigurationIAPage /></Suspense>} />
        <Route path="personnalisation-documents" element={<Suspense fallback={<LazyLoadingSpinner />}><PersonnalisationDocumentsPage /></Suspense>} />
        <Route path="data-sync" element={<Suspense fallback={<LazyLoadingSpinner />}><DataSyncPage /></Suspense>} />
      </Route>

      {/* Agent Routes */}
      <Route
        path="/agent"
        element={
          <ProtectedRoute requiredRole="agent">
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<LazyLoadingSpinner />}><AgentDashboard /></Suspense>} />
        <Route path="recherche" element={<Suspense fallback={<LazyLoadingSpinner />}><RechercheUnifiee /></Suspense>} />
        <Route path="prospection" element={<Suspense fallback={<LazyLoadingSpinner />}><MaProspectionPage /></Suspense>} />
        <Route path="agenda" element={<Suspense fallback={<LazyLoadingSpinner />}><AgendaPage /></Suspense>} />
        <Route path="coproprietes" element={<Suspense fallback={<LazyLoadingSpinner />}><AgentCoproprietesPage /></Suspense>} />
        <Route path="opportunites" element={<Suspense fallback={<LazyLoadingSpinner />}><AgentOpportunitesPage /></Suspense>} />
        <Route path="profil" element={<Suspense fallback={<LazyLoadingSpinner />}><AgentProfilPage /></Suspense>} />
      </Route>

      {/* Travaux Routes - Admin/Super Admin only */}
      <Route
        path="/travaux"
        element={
          <TravauxProtectedRoute>
            <MainLayout />
          </TravauxProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<LazyLoadingSpinner />}><TravauxDashboard /></Suspense>} />
        <Route path="projets" element={<Suspense fallback={<LazyLoadingSpinner />}><ProjetsTravauxPage /></Suspense>} />
        <Route path="projets/:id" element={<Suspense fallback={<LazyLoadingSpinner />}><ProjetTravauxDetailPage /></Suspense>} />
        <Route path="projets/:id/agenda" element={<Suspense fallback={<LazyLoadingSpinner />}><AgendaChantierPage /></Suspense>} />
        <Route path="sous-traitants" element={<Suspense fallback={<LazyLoadingSpinner />}><SousTraitantsPage /></Suspense>} />
        <Route path="delegataires-cee" element={<Suspense fallback={<LazyLoadingSpinner />}><DelegatairesCEEPage /></Suspense>} />
        <Route path="licences" element={<Suspense fallback={<LazyLoadingSpinner />}><LicenceTravauxPage /></Suspense>} />
        <Route path="planning" element={<Suspense fallback={<LazyLoadingSpinner />}><PlanningGanttPage /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<LazyLoadingSpinner />}><DocumentsGEDPage /></Suspense>} />
      </Route>

      {/* Default Redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <MobileBottomNav />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
